import { useEffect, useMemo, useRef, useState } from "react";
import { Eye, EyeOff, Shuffle, Snowflake } from "lucide-react";
import { motion } from "motion/react";
import {
  equipCosmetic as applyCosmetic,
  MASTER_CRAFT_REWARD_COINS,
  playerLevelProgress,
  XP_REWARD_COINS,
  type CosmeticCategory,
  type CosmeticLoadout
} from "@craftoff/game-core";
import {
  claimAccountProfile,
  clearPendingAccountClaim,
  createAccountWithPassword,
  completeRewardedAdSession,
  equipShopItem,
  loadLeaderboard,
  loadAccountSession,
  loadAccountStats,
  loadShop,
  pendingAccountClaim,
  purchaseShopItem,
  signInWithPassword,
  startRewardedAdSession,
  type AccountSession,
  type AccountStats,
  type LeaderboardEntry,
  type SpecialItemCounts,
  type ShopState
} from "./account-api";
import {
  activityMinutes,
  buildActivityCalendar,
  monthLabelDates,
  playedTimeLabel,
  winLossRatioLabel,
  winRateLabel
} from "./account-stats";
import { AvatarFaceplate, CosmeticPreview } from "./Cosmetics";
import { LegalLinks } from "./LegalViews";
import { showRewardedAd } from "./rewarded-ads";
import { ADS_ENABLED } from "./ad-config";

function friendlyError(error: unknown): string {
  const code = error instanceof Error ? error.message : "REQUEST_FAILED";
  const messages: Record<string, string> = {
    INVALID_USERNAME: "Use 3–16 letters, numbers, spaces, or underscores.",
    USERNAME_TAKEN: "That public name is already taken.",
    INVALID_EMAIL_OR_PASSWORD: "That email or password is incorrect.",
    USER_ALREADY_EXISTS: "An account with that email already exists.",
    PASSWORD_TOO_SHORT: "Your password must be at least 8 characters.",
    PASSWORD_TOO_LONG: "Your password must be no more than 128 characters.",
    PASSWORD_NEEDS_SPECIAL_CHARACTER: "Add a special character to make your password stronger.",
    PASSWORD_NOT_STRONG_ENOUGH: "Use uppercase, lowercase, a number, and a special character.",
    TOO_MANY_REQUESTS: "Too many attempts. Please wait a moment and try again.",
    ACCOUNT_CLAIM_FAILED: "We could not finish creating the profile."
  };
  return messages[code] ?? "Something went wrong. Please try again.";
}

const passwordChecks = [
  { label: "8+ characters", test: (password: string) => password.length >= 8 },
  { label: "Uppercase letter", test: (password: string) => /[A-Z]/.test(password) },
  { label: "Lowercase letter", test: (password: string) => /[a-z]/.test(password) },
  { label: "Number", test: (password: string) => /\d/.test(password) },
  { label: "Special character", test: (password: string) => /[^A-Za-z0-9\s]/.test(password) }
];

function PasswordField({ id, label, value, onChange, autoComplete }: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: "current-password" | "new-password";
}) {
  const [visible, setVisible] = useState(false);
  return (
    <label className="account-field" htmlFor={id}>
      <span>{label}</span>
      <div className="password-input">
        <input
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          autoComplete={autoComplete}
          minLength={8}
          maxLength={128}
          required
        />
        <button type="button" onClick={() => setVisible((current) => !current)} aria-label={`${visible ? "Hide" : "Show"} ${label.toLowerCase()}`}>
          {visible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
        </button>
      </div>
    </label>
  );
}

export function AccountDialog({ account, onClose, onComplete }: {
  account: AccountSession | null;
  onClose: () => void;
  onComplete: (session: AccountSession) => void;
}) {
  const pending = pendingAccountClaim();
  const [username, setUsername] = useState(pending?.username ?? "");
  const [mode, setMode] = useState<"sign-in" | "create">("sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profileRequired, setProfileRequired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const validUsername = /^[A-Za-z0-9_ ]{3,16}$/.test(username.trim());
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const strongPassword = passwordChecks.every(({ test }) => test(password));
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const choosingUsername = Boolean(account?.authenticated || profileRequired);

  const finishClaim = async () => {
    if (!validUsername) {
      setError("Use 3–16 letters, numbers, spaces, or underscores.");
      return;
    }
    const session = await claimAccountProfile(username.trim());
    clearPendingAccountClaim();
    onComplete(session);
  };

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try { await action(); }
    catch (caught) { setError(friendlyError(caught)); }
    finally { setBusy(false); }
  };

  const finishAuthentication = async () => {
    const session = await loadAccountSession();
    if (session.needsUsername) {
      setProfileRequired(true);
      return;
    }
    onComplete(session);
  };

  const submitCredentials = async () => {
    if (mode === "create" && (!strongPassword || !passwordsMatch)) return;
    await (mode === "create"
      ? createAccountWithPassword(email.trim(), password)
      : signInWithPassword(email.trim(), password));
    await finishAuthentication();
  };

  const switchMode = (nextMode: "sign-in" | "create") => {
    setMode(nextMode);
    setPassword("");
    setConfirmPassword("");
    setError("");
  };

  return (
    <div className="account-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <motion.section className="account-modal" role="dialog" aria-modal="true" aria-labelledby="account-title" initial={{ opacity: 0, scale: .92 }} animate={{ opacity: 1, scale: 1 }}>
        <button className="account-close" onClick={onClose} aria-label="Close account dialog">×</button>
        <h1 id="account-title">{choosingUsername ? "Choose your public name" : mode === "sign-in" ? "Sign in" : "Create account"}</h1>
        <p>{choosingUsername ? "This is the only name other players will see." : mode === "sign-in" ? "Sign in to access your saved progress." : "Create an account to earn XP in future Quick Play matches."}</p>

        {choosingUsername ? (
          <>
            <label className="account-field">
              <span>PUBLIC USERNAME</span>
              <input value={username} onChange={(event) => setUsername(event.target.value)} maxLength={16} autoComplete="nickname" placeholder="Craft name" />
            </label>
            <button className="minecraft-button account-submit" disabled={busy || !validUsername} onClick={() => void run(finishClaim)}>CREATE PROFILE</button>
          </>
        ) : (
          <>
            <div className="account-auth-tabs" role="tablist" aria-label="Account access">
              <button type="button" role="tab" aria-selected={mode === "sign-in"} className={mode === "sign-in" ? "active" : ""} onClick={() => switchMode("sign-in")}>SIGN IN</button>
              <button type="button" role="tab" aria-selected={mode === "create"} className={mode === "create" ? "active" : ""} onClick={() => switchMode("create")}>CREATE ACCOUNT</button>
            </div>
            <form className="account-form" onSubmit={(event) => { event.preventDefault(); void run(submitCredentials); }}>
              <label className="account-field">
                <span>EMAIL</span>
                <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" placeholder="you@example.com" required />
              </label>
              <PasswordField id="account-password" label="PASSWORD" value={password} onChange={setPassword} autoComplete={mode === "sign-in" ? "current-password" : "new-password"} />
              {mode === "create" && (
                <>
                  <PasswordField id="account-confirm-password" label="CONFIRM PASSWORD" value={confirmPassword} onChange={setConfirmPassword} autoComplete="new-password" />
                  <div className="password-requirements" aria-label="Password requirements">
                    {passwordChecks.map(({ label, test }) => <span className={test(password) ? "met" : ""} key={label}>{test(password) ? "✓" : "·"} {label}</span>)}
                    <span className={passwordsMatch ? "met" : ""}>{passwordsMatch ? "✓" : "·"} Passwords match</span>
                  </div>
                </>
              )}
              <button type="submit" className="minecraft-button account-submit" disabled={busy || !validEmail || !password || (mode === "create" && (!strongPassword || !passwordsMatch))}>
                {busy ? "PLEASE WAIT…" : mode === "sign-in" ? "SIGN IN" : "CREATE ACCOUNT"}
              </button>
            </form>
          </>
        )}
        {error && <p className="account-error" role="alert">{error}</p>}
        <small className="account-privacy">Your email is private. Only your chosen username appears in game.</small>
        <LegalLinks accountNotice />
      </motion.section>
    </div>
  );
}

function activityDescription(date: string, playedMs: number, matchCount: number): string {
  const minutes = activityMinutes(playedMs);
  const day = new Date(`${date}T12:00:00`).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  if (matchCount === 0) return `No matches on ${day}`;
  return `${minutes} minute${minutes === 1 ? "" : "s"} across ${matchCount} match${matchCount === 1 ? "" : "es"} on ${day}`;
}

function Heatmap({ stats }: { stats: AccountStats }) {
  const cells = useMemo(() => buildActivityCalendar(stats), [stats]);
  const labels = useMemo(() => monthLabelDates(cells), [cells]);
  return (
    <div className="profile-activity-wrap">
      <div className="profile-heatmap-scroll">
        <div className="profile-heatmap-frame">
          <div className="profile-heatmap-months" aria-hidden="true">
            {labels.map((date, index) => <span key={`${index}-${date ?? ""}`}>{date ? new Date(`${date}T12:00:00`).toLocaleDateString([], { month: "short" }) : ""}</span>)}
          </div>
          <div className="profile-heatmap-body">
            <div className="profile-heatmap-days" aria-hidden="true"><span /><span>MON</span><span /><span>WED</span><span /><span>FRI</span><span /></div>
            <section className="profile-activity" aria-label="Match activity">
              <div className="profile-heatmap-grid" role="grid" aria-label={`Match activity from ${stats.rangeStart} through ${stats.rangeEnd}`}>
                {cells.map((cell) => {
                  const description = activityDescription(cell.date, cell.playedMs, cell.matchCount);
                  return (
                    <span
                      className={`profile-heatmap-cell level-${cell.level} ${cell.inRange ? "" : "outside-range"}`}
                      role="gridcell"
                      aria-label={cell.inRange ? description : undefined}
                      aria-hidden={cell.inRange ? undefined : true}
                      title={cell.inRange ? description : undefined}
                      key={cell.date}
                    />
                  );
                })}
              </div>
            </section>
          </div>
        </div>
      </div>
      <div className="profile-heatmap-legend" aria-label="Activity intensity from less to more">
        <span>LESS</span>{[0, 1, 2, 3, 4].map((level) => <i className={`level-${level}`} key={level} />)}<span>MORE</span>
      </div>
    </div>
  );
}

export function ProfileStatsModal({ account, onClose, onSignOut }: {
  account: AccountSession;
  onClose: () => void;
  onSignOut: () => Promise<boolean>;
}) {
  const [stats, setStats] = useState<AccountStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState(false);
  const closeButton = useRef<HTMLButtonElement>(null);
  const priorFocus = useRef<HTMLElement | null>(null);

  const load = () => {
    setLoading(true);
    setLoadError(false);
    setStats(null);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    void loadAccountStats(timeZone).then(setStats).catch(() => setLoadError(true)).finally(() => setLoading(false));
  };

  useEffect(() => {
    priorFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButton.current?.focus();
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !signingOut) onClose();
    };
    window.addEventListener("keydown", escape);
    return () => {
      window.removeEventListener("keydown", escape);
      priorFocus.current?.focus();
    };
  }, [onClose, signingOut]);

  useEffect(load, []);

  const signOut = async () => {
    if (account.development) return;
    setSigningOut(true);
    setSignOutError(false);
    const signedOut = await onSignOut();
    if (!signedOut) {
      setSignOutError(true);
      setSigningOut(false);
    }
  };
  const level = playerLevelProgress(account.xp);
  const cards = stats ? [
    ["MATCHES", stats.totalMatches.toLocaleString()],
    ["WINS", stats.wins.toLocaleString()],
    ["LOSSES", stats.losses.toLocaleString()],
    ["WIN RATE", winRateLabel(stats.wins, stats.losses)],
    ["W/L RATIO", winLossRatioLabel(stats.wins, stats.losses)],
    ["HOURS PLAYED", playedTimeLabel(stats.totalPlayedMs)],
    ["ACTIVE DAYS", stats.activeDays.toLocaleString()]
  ] : [];

  return (
    <div className="account-modal-backdrop profile-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !signingOut) onClose(); }}>
      <motion.section className="profile-modal" role="dialog" aria-modal="true" aria-labelledby="profile-title" initial={{ opacity: 0, scale: .95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}>
        <button ref={closeButton} className="account-close" onClick={onClose} disabled={signingOut} aria-label="Close profile">×</button>
        <header className="profile-header">
          <AvatarFaceplate faceplateId={account.cosmetics.faceplateId} />
          <div>
            <small>CRAFTOFF PROFILE</small>
            <h1 id="profile-title">{account.username}</h1>
            {account.email && <span>{account.email}</span>}
          </div>
          <div className="profile-progression">
            <span><small>LEVEL</small><b>{level.level}</b></span>
            <span><small>XP</small><b>{account.xp.toLocaleString()}</b></span>
            <span><small>RATING</small><b>{account.rankedRating.toLocaleString()}</b></span>
          </div>
        </header>

        {loading && <div className="profile-state" aria-live="polite"><b>LOADING ACCOUNT STATS…</b><span>Checking your match history.</span></div>}
        {loadError && !loading && (
          <div className="profile-state error" role="alert">
            <b>STATS COULD NOT LOAD</b><span>Your account is still available.</span>
            <button className="minecraft-button" onClick={load}>TRY AGAIN</button>
          </div>
        )}
        {stats && !loading && (
          <>
            <div className="profile-stat-grid">
              {cards.map(([label, value]) => <article key={label}><small>{label}</small><strong>{value}</strong></article>)}
            </div>
            {stats.totalMatches === 0
              ? <div className="profile-state empty"><b>NO RECORDED MATCHES YET</b><span>Finish an online Quick or Ranked match to start your activity chart.</span></div>
              : <Heatmap stats={stats} />}
          </>
        )}

        <footer className="profile-footer">
          <span>Your email stays private and is never shown to other players.</span>
          <button className="profile-sign-out" disabled={signingOut || account.development} onClick={() => void signOut()}>
            {account.development ? "LOCAL DEV ACCOUNT" : signingOut ? "SIGNING OUT…" : "SIGN OUT"}
          </button>
        </footer>
        {signOutError && <p className="profile-signout-error" role="alert">SIGN OUT FAILED. YOUR ACCOUNT IS STILL ACTIVE.</p>}
      </motion.section>
    </div>
  );
}

function AccountPage({ title, subtitle, onBack, billboard = false, wide = false, children }: {
  title: string;
  subtitle: string;
  onBack: () => void;
  billboard?: boolean;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <motion.section className="account-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <header className={billboard ? "account-page-billboard-header" : undefined}>
        <button className="exit-button" onClick={onBack}>← MENU</button>
        <div className={billboard ? "account-page-billboard" : undefined}>
          {!billboard && <span>CRAFTOFF</span>}
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
      </header>
      <div className={`account-page-body ${wide ? "wide" : ""}`}>{children}</div>
    </motion.section>
  );
}

export function LeaderboardScreen({ currentUsername, onBack }: { currentUsername: string; onBack: () => void }) {
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => { void loadLeaderboard().then(setEntries).catch(() => setError(true)); }, []);
  return (
    <AccountPage title="Leaderboard" subtitle="The strongest registered crafters" onBack={onBack} billboard>
      {error && <p className="empty-state">THE LEADERBOARD COULD NOT LOAD.</p>}
      {!entries && !error && <p className="empty-state">LOADING RANKINGS…</p>}
      {entries && !entries.length && <p className="empty-state">NO RANKED CRAFTERS YET. YOU COULD BE FIRST.</p>}
      {entries && entries.length > 0 && (
        <div className="leaderboard-table" role="table" aria-label="Craftoff leaderboard">
          <div className="leaderboard-row heading" role="row"><span>#</span><span>PLAYER</span><span>XP</span><span>W–L</span></div>
          {entries.map((entry, index) => (
            <div className={`leaderboard-row ${entry.username === currentUsername ? "current" : ""}`} role="row" key={entry.username}>
              <strong>{index + 1}</strong><span>{entry.username}</span><b>{entry.xp}</b><small>{entry.wins}–{entry.losses}</small>
            </div>
          ))}
        </div>
      )}
    </AccountPage>
  );
}

export type ShopCategory = "all" | CosmeticCategory | "special";

const SHOP_CATEGORIES: Array<{ id: ShopCategory; label: string; shortLabel: string }> = [
  { id: "all", label: "All items", shortLabel: "All" },
  { id: "special", label: "Special items", shortLabel: "Items" },
  { id: "wallpaper", label: "Wallpapers", shortLabel: "Worlds" },
  { id: "crafting-table", label: "Crafting tables", shortLabel: "Tables" },
  { id: "faceplate", label: "Avatar faceplates", shortLabel: "Faceplates" }
];

function SpecialItemPreview({ itemId, quantity }: { itemId: string; quantity: number }) {
  if (itemId === "freeze") {
    return (
      <span className="special-item-preview freeze-preview" aria-hidden="true">
        <Snowflake className="freeze-preview-icon" strokeWidth={2.6} />
        <strong>×{quantity}</strong>
      </span>
    );
  }
  if (itemId === "scramble") {
    return (
      <span className="special-item-preview scramble-preview" aria-hidden="true">
        <Shuffle className="scramble-preview-icon" strokeWidth={2.8} />
        <strong>×{quantity}</strong>
      </span>
    );
  }
  return (
    <span className="special-item-preview" aria-hidden="true">
      <i className="blueprint-sheet">
        <b /><b className="hinted" /><b />
        <b /><b className="hinted" /><b />
        <b /><b className="hinted" /><b />
      </i>
      <strong>×{quantity}</strong>
    </span>
  );
}

export function ShopScreen({ initialCategory = "all", onLoadoutChange, onCoinBalanceChange, onSpecialItemsChange, onBack }: {
  initialCategory?: ShopCategory;
  onLoadoutChange: (loadout: CosmeticLoadout) => void;
  onCoinBalanceChange: (coinBalance: number) => void;
  onSpecialItemsChange: (counts: SpecialItemCounts) => void;
  onBack: () => void;
}) {
  const [shop, setShop] = useState<ShopState | null>(null);
  const [category, setCategory] = useState<ShopCategory>(initialCategory);
  const [equippingId, setEquippingId] = useState<string | null>(null);
  const [buyingId, setBuyingId] = useState<string | null>(null);
  const [watchingAd, setWatchingAd] = useState(false);
  const [earnOpen, setEarnOpen] = useState(false);
  const [shortageItemId, setShortageItemId] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState(false);
  useEffect(() => {
    void loadShop().then((nextShop) => {
      setShop(nextShop);
      onLoadoutChange(nextShop.loadout);
      onCoinBalanceChange(nextShop.coinBalance);
      onSpecialItemsChange(nextShop.specialItemCounts);
    }).catch(() => setError(true));
  }, [onCoinBalanceChange, onLoadoutChange, onSpecialItemsChange]);

  const visibleItems = shop?.items.filter((item) => category === "all" || item.category === category) ?? [];
  const equip = async (itemId: string) => {
    if (!shop) return;
    const previousLoadout = shop.loadout;
    const optimisticLoadout = applyCosmetic(previousLoadout, itemId);
    const applyLoadout = (nextLoadout: CosmeticLoadout) => {
      setShop((current) => current ? {
        ...current,
        loadout: nextLoadout,
        items: current.items.map((item) => item.category === "special" ? item : ({
          ...item,
          equipped: item.id === nextLoadout.wallpaperId || item.id === nextLoadout.craftingTableId || item.id === nextLoadout.faceplateId
        }))
      } : current);
      onLoadoutChange(nextLoadout);
    };
    setShortageItemId(null);
    setEquippingId(itemId);
    setNotice("");
    applyLoadout(optimisticLoadout);
    try {
      const nextLoadout = await equipShopItem(itemId);
      applyLoadout(nextLoadout);
      setNotice("EQUIPPED — YOUR LOADOUT IS READY");
    } catch {
      applyLoadout(previousLoadout);
      setNotice("COULD NOT EQUIP THAT ITEM. TRY AGAIN.");
    } finally {
      setEquippingId(null);
    }
  };
  const buy = async (itemId: string) => {
    if (!shop || buyingId !== null) return;
    const previousShop = shop;
    const purchasedItem = previousShop.items.find((item) => item.id === itemId);
    if (!purchasedItem) return;
    const special = purchasedItem.category === "special";
    const optimisticCounts = special
      ? {
          ...previousShop.specialItemCounts,
          [itemId]: (previousShop.specialItemCounts[itemId] ?? 0) + 1
        }
      : previousShop.specialItemCounts;
    const optimisticBalance = previousShop.wallet.unlimitedCoins
      ? previousShop.coinBalance
      : previousShop.coinBalance - purchasedItem.coinPrice;
    const optimisticShop: ShopState = {
      ...previousShop,
      coinBalance: optimisticBalance,
      wallet: { ...previousShop.wallet, coinBalance: optimisticBalance },
      specialItemCounts: optimisticCounts,
      items: previousShop.items.map((item) => item.id !== itemId
        ? item
        : item.category === "special"
          ? {
              ...item,
              quantity: optimisticCounts[item.id] ?? 0,
              owned: (optimisticCounts[item.id] ?? 0) > 0
            }
          : { ...item, owned: true })
    };
    setShortageItemId(null);
    setBuyingId(itemId);
    setNotice("");
    setShop(optimisticShop);
    onCoinBalanceChange(optimisticBalance);
    onSpecialItemsChange(optimisticCounts);
    try {
      const nextShop = await purchaseShopItem(itemId, previousShop);
      setShop(nextShop);
      onCoinBalanceChange(nextShop.coinBalance);
      onSpecialItemsChange(nextShop.specialItemCounts);
      setNotice(special ? "" : "PURCHASE COMPLETE — THIS COSMETIC IS YOURS");
    } catch (purchaseError) {
      setShop(previousShop);
      onCoinBalanceChange(previousShop.coinBalance);
      onSpecialItemsChange(previousShop.specialItemCounts);
      setNotice(purchaseError instanceof Error && purchaseError.message === "INSUFFICIENT_COINS"
        ? "NOT ENOUGH COINS FOR THAT ITEM."
        : "COULD NOT COMPLETE THAT PURCHASE. TRY AGAIN.");
    } finally {
      setBuyingId(null);
    }
  };
  const watchAd = async () => {
    if (!shop) return;
    setWatchingAd(true);
    setNotice("");
    try {
      const session = await startRewardedAdSession();
      await showRewardedAd(shop.rewardedAds.adUnitPath);
      const completion = await completeRewardedAdSession(session.sessionId);
      setShop((current) => current ? {
        ...current,
        coinBalance: completion.wallet.coinBalance,
        wallet: completion.wallet
      } : current);
      onCoinBalanceChange(completion.wallet.coinBalance);
      setNotice(`AD COMPLETE — +${completion.coinsGranted} COINS`);
    } catch (adError) {
      setNotice(adError instanceof Error && adError.message === "REWARDED_AD_NOT_COMPLETED"
        ? "AD CLOSED — NO ATTEMPT USED."
        : "THE REWARDED AD IS UNAVAILABLE. TRY AGAIN LATER.");
    } finally {
      setWatchingAd(false);
    }
  };

  return (
    <AccountPage title="Craftoff Shop" subtitle="Build your look. Stock your battle kit." onBack={onBack} billboard wide>
      {error && <p className="empty-state">THE SHOP COULD NOT LOAD.</p>}
      {!shop && !error && <p className="empty-state">OPENING YOUR LOCKER…</p>}
      {shop && (
        <>
          <div className="shop-toolbar">
            <div className="shop-category-tabs" role="tablist" aria-label="Shop categories">
              {SHOP_CATEGORIES.map((option) => (
                <button key={option.id} role="tab" aria-selected={category === option.id} className={category === option.id ? "active" : ""} onClick={() => { setCategory(option.id); setShortageItemId(null); }}>
                  <span>{option.label}</span><small>{option.shortLabel}</small>
                </button>
              ))}
            </div>
            <div className="shop-wallet-actions">
              <div className="coin-balance-chip shop-wallet" aria-label={shop.wallet.unlimitedCoins ? "Unlimited coins" : `${shop.coinBalance} coins`}><i aria-hidden="true">◆</i><b>{shop.wallet.unlimitedCoins ? "∞" : shop.coinBalance.toLocaleString()}</b></div>
              <button
                className={`shop-earn-toggle ${earnOpen ? "active" : ""}`}
                aria-label="Earn coins"
                aria-expanded={earnOpen}
                aria-controls="shop-earn-popover"
                onClick={() => setEarnOpen((current) => !current)}
              >
                <span>EARN</span><i aria-hidden="true">◆</i>
              </button>
              {earnOpen && (
                <section id="shop-earn-popover" className="shop-earn-popover" aria-label="Ways to earn coins">
                  <header>
                    <div><strong>EARN COINS</strong><small>BUILD YOUR WALLET</small></div>
                    <button className="shop-earn-close" aria-label="Close earn coins panel" onClick={() => setEarnOpen(false)}>×</button>
                  </header>
                  <div className="shop-earn-option">
                    <div><b>XP MILESTONE</b><span>{shop.wallet.xpRemaining.toLocaleString()} XP until {shop.wallet.nextXpMilestone.toLocaleString()} XP</span></div>
                    <em>◆ {XP_REWARD_COINS}</em>
                  </div>
                  <div className="shop-earn-option">
                    <div><b>MASTER CRAFTS</b><span>{shop.wallet.masterCraftsToday}/{shop.wallet.masterCraftDailyLimit} rewarded today</span></div>
                    <em>◆ {MASTER_CRAFT_REWARD_COINS} EACH</em>
                  </div>
                  {ADS_ENABLED && (
                    <div className="shop-earn-option shop-earn-ad">
                      <div>
                        <b>REWARDED AD</b>
                        <span>{shop.wallet.rewardedAdDailyLimit - shop.wallet.rewardedAdsToday}/{shop.wallet.rewardedAdDailyLimit} remaining · resets {new Date(shop.wallet.dailyResetAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                      </div>
                      <button
                        disabled={!shop.rewardedAds.enabled || watchingAd || shop.wallet.rewardedAdsToday >= shop.wallet.rewardedAdDailyLimit}
                        onClick={() => void watchAd()}
                      >
                        {!shop.rewardedAds.enabled ? "UNAVAILABLE" : watchingAd ? "OPENING…" : shop.wallet.rewardedAdsToday >= shop.wallet.rewardedAdDailyLimit ? "LIMIT REACHED" : `WATCH · ◆ ${shop.rewardedAds.rewardCoins}`}
                      </button>
                    </div>
                  )}
                </section>
              )}
            </div>
          </div>
          {notice && <p className={`shop-equip-notice ${notice.startsWith("COULD") ? "error" : ""}`} role="status">{notice}</p>}
          <div className="shop-grid">
            {visibleItems.map((item) => {
              const special = item.category === "special";
              const unaffordable = !shop.wallet.unlimitedCoins && (special || !item.owned) && shop.coinBalance < item.coinPrice;
              return (
                <article className={`shop-card rarity-${item.rarity} ${item.equipped ? "equipped" : ""} ${special ? "special-item-card" : ""}`} data-shop-item-id={item.id} key={item.id}>
                  {special ? <SpecialItemPreview itemId={item.id} quantity={item.quantity} /> : <CosmeticPreview item={item} />}
                  <div className="shop-card-copy">
                    <div><small>{special ? "battle item" : item.category === "crafting-table" ? "crafting table" : item.category.replace("-", " ")}</small><span>{item.rarity}</span></div>
                    <h2>{item.name}</h2>
                    {special && <p className="shop-item-description">{item.description}</p>}
                    {!special && item.creator && item.sourceUrl && (
                      <a className="shop-card-attribution" href={item.sourceUrl} target="_blank" rel="noreferrer">
                        ART BY {item.creator} · {item.license ?? "SOURCE"} ↗
                      </a>
                    )}
                  </div>
                  {shortageItemId === item.id && (
                    <div className="shop-shortage-popover" role="alert">
                      <b>NOT ENOUGH COINS</b>
                      <span>YOU NEED ◆ {(item.coinPrice - shop.coinBalance).toLocaleString()} MORE</span>
                      <button onClick={() => setShortageItemId(null)}>OK</button>
                    </div>
                  )}
                  <button
                    className={`shop-equip-button ${unaffordable ? "unaffordable" : ""}`}
                    disabled={item.equipped || equippingId !== null || buyingId !== null}
                    onClick={() => {
                      if (special && unaffordable) setShortageItemId(item.id);
                      else if (special) void buy(item.id);
                      else if (item.owned) void equip(item.id);
                      else if (unaffordable) setShortageItemId(item.id);
                      else void buy(item.id);
                    }}
                  >
                    {special
                      ? buyingId === item.id
                        ? "PURCHASING…"
                        : <span className="shop-button-price"><i aria-hidden="true">◆</i><span>{item.coinPrice.toLocaleString()}</span></span>
                      : item.equipped
                      ? "✓ EQUIPPED"
                      : equippingId === item.id
                        ? "EQUIPPING…"
                        : buyingId === item.id
                          ? "PURCHASING…"
                          : item.owned
                            ? "EQUIP"
                            : <span className="shop-button-price"><i aria-hidden="true">◆</i><span>{item.coinPrice.toLocaleString()}</span></span>}
                  </button>
                </article>
              );
            })}
          </div>
        </>
      )}
    </AccountPage>
  );
}
